import { z } from "zod";
import { UpdateDeviceRequestSchema } from "./devices.js";
import {
  CreateCardObjectSchema,
  UpdateCardObjectSchema,
  cardCreateHasSeparator,
  cardUpdateSeparatorOk,
  CARD_SEPARATOR_REFINE,
} from "./cards.js";
import { CreateSensorObjectSchema, scaleSuperRefine } from "./sensors.js";
import { UpdateSensorRequestSchema } from "./sensors.js";
import { type CommandSpec, Uuid, hasUpdate, UPDATE_MSG } from "./assistant-catalog-types.js";

// Família Admin de dispositivos (action · GESTOR). Fecha o cadastro da árvore de
// aquisição: editar CLP + CRUD de cartões de I/O e de sensores/posições. Reusa EXATAMENTE os services
// da tela /devices via dispatch. Args compõem os OBJETOS base do wire (.extend + .strict()), re-
// aplicando os refines cross-field (ANALOG⇒separator, escala 4-ou-0) que são load-bearing — os
// services de create NÃO os revalidam. Exclusões (`card.delete`, `sensor.delete`) são DESTRUTIVAS
// (confirmação por nome). `deviceId`/`cardId` viajam nos args p/ a invalidação de cache no web.

export const DEVICES_ADMIN_COMMANDS: readonly CommandSpec[] = [
  {
    name: "device.update",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Edita o código/nome de um CLP (só os campos informados).",
    argsSchema: UpdateDeviceRequestSchema.extend({ deviceId: Uuid })
      .strict()
      .refine(hasUpdate(["deviceId"]), UPDATE_MSG),
  },
  {
    name: "card.create",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Cria um cartão de I/O sob um CLP (cartão ANALOG exige separador).",
    argsSchema: CreateCardObjectSchema.extend({ deviceId: Uuid })
      .strict()
      .refine(cardCreateHasSeparator, CARD_SEPARATOR_REFINE),
  },
  {
    name: "card.update",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Edita SÓ os campos informados de um cartão (ANALOG continua exigindo separador).",
    argsSchema: UpdateCardObjectSchema.extend({ deviceId: Uuid, cardId: Uuid })
      .strict()
      .refine(cardUpdateSeparatorOk, CARD_SEPARATOR_REFINE)
      .refine(hasUpdate(["deviceId", "cardId"]), UPDATE_MSG),
  },
  {
    name: "card.delete",
    kind: "action",
    riskTier: "destructive",
    minRole: "GESTOR",
    description: "Exclui um cartão de I/O (bloqueado se houver sensor ativo).",
    argsSchema: z.object({ deviceId: Uuid, cardId: Uuid }).strict(),
  },
  {
    name: "sensor.create",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description:
      "Cria um sensor/posição num cartão (a escala analógica exige rawMin/rawMax/engMin/engMax juntos ou nenhum).",
    argsSchema: CreateSensorObjectSchema.extend({ deviceId: Uuid })
      .strict()
      .superRefine(scaleSuperRefine),
  },
  {
    name: "sensor.update",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Edita SÓ código/nome/tipo de um sensor (posição/escala não são editáveis).",
    argsSchema: UpdateSensorRequestSchema.extend({ deviceId: Uuid, sensorId: Uuid })
      .strict()
      .refine(hasUpdate(["deviceId", "sensorId"]), UPDATE_MSG),
  },
  {
    name: "sensor.delete",
    kind: "action",
    riskTier: "destructive",
    minRole: "GESTOR",
    description: "Exclui um sensor/posição (bloqueado se estiver vinculado a um parâmetro).",
    argsSchema: z.object({ deviceId: Uuid, sensorId: Uuid }).strict(),
  },
];
